/**
 * One-of-N choice as a row of toggle buttons (`aria-pressed`): what the pickers use instead of
 * a `<select>`, so arrows, Enter and the visible focus behave the same everywhere.
 */

import './chrome.css';
import { button, el } from '../dom';

export interface Choice<T> {
  value: T;
  label: string;
  /** Tooltip: the description from `GameApi.describe`. */
  title?: string;
}

export interface ChoiceRow<T> {
  readonly el: HTMLElement;
  readonly buttons: readonly HTMLButtonElement[];
  value(): T;
  /** Select without firing `onChange`. */
  set(value: T): void;
  /** Replace the options (weather depends on terrain); keeps the value when it still exists. */
  setChoices(choices: ReadonlyArray<Choice<T>>): void;
}

export function choiceRow<T>(
  label: string,
  initialChoices: ReadonlyArray<Choice<T>>,
  initial: T,
  onChange: (value: T) => void,
): ChoiceRow<T> {
  const root = el('div', 'menu-choice');
  const group = el('div', 'menu-choice-group');
  group.setAttribute('role', 'group');
  group.setAttribute('aria-label', label);
  root.append(el('span', 'menu-field-label', label), group);

  let choices = initialChoices;
  let current = initial;
  let buttons: HTMLButtonElement[] = [];

  const paint = (): void => {
    buttons.forEach((b, i) => b.setAttribute('aria-pressed', choices[i]?.value === current ? 'true' : 'false'));
  };

  const build = (): void => {
    buttons = choices.map((choice) => {
      const b = button(
        choice.label,
        () => {
          current = choice.value;
          paint();
          onChange(current);
        },
        'menu-btn-small',
      );
      if (choice.title !== undefined) b.title = choice.title;
      return b;
    });
    group.replaceChildren(...buttons);
    paint();
  };
  build();

  return {
    el: root,
    get buttons() {
      return buttons;
    },
    value: () => current,
    set(value): void {
      current = value;
      paint();
    },
    setChoices(next): void {
      choices = next;
      const first = next[0];
      if (!next.some((c) => c.value === current) && first !== undefined) current = first.value;
      build();
    },
  };
}
